import React, { useState } from 'react';
import { Button, Modal } from '@appkit4/react-components';
import { Input } from '@appkit4/react-components/field';
import styles from './CursorInstructionsModal.module.scss';
import { generateDeeplink } from './mcpInstallDeepLinkGenerator';

interface CursorInstructionsModalProps {
    visible: boolean;
    onCancel: () => void;
    onOpenInCursor: (projectDir: string) => void;
}

const CursorInstructionsModal: React.FC<CursorInstructionsModalProps> = ({ visible, onCancel, onOpenInCursor }) => {
    const [copied, setCopied] = useState(false);
    const [projectDir, setProjectDir] = useState(() => {
        // Initialize from localStorage or default to "/"
        const saved = localStorage.getItem('rapidcraft-project-dir');
        return saved || '/';
    });
    const [mcpDeeplink, setMcpDeeplink] = useState('');

    const npmLoginCommand =
        'npm login --registry=https://artifacts-west.pwc.com/artifactory/api/npm/g00020-pwc-gx-digital-appkit-npm';

    const handleCopyCommand = async () => {
        try {
            await navigator.clipboard.writeText(npmLoginCommand);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy command:', err);
        }
    };

    const handleGenerateMcpDeeplink = () => {
        if (projectDir.trim()) {
            const result = generateDeeplink({ projectDir: projectDir.trim() });
            setMcpDeeplink(result.deeplink);
        }
    };

    const handleCopyMcpDeeplink = async () => {
        if (mcpDeeplink) {
            try {
                await navigator.clipboard.writeText(mcpDeeplink);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error('Failed to copy MCP deeplink:', err);
            }
        }
    };

    const handleProjectDirChange = (value: string) => {
        setProjectDir(value);
        // Save to localStorage for persistence
        localStorage.setItem('rapidcraft-project-dir', value);
    };

    return (
        <Modal
            visible={visible}
            onCancel={onCancel}
            title="Open in Cursor Instructions"
            modalStyle={{ width: '600px' }}
            footer={[
                <Button key="openInCursor" kind="primary" onClick={() => onOpenInCursor(projectDir)}>
                    Open in Cursor and Paste Prompt
                </Button>,
                <Button key="close" kind="text" onClick={onCancel}>
                    Close
                </Button>,
            ]}>
            <div className={styles.content}>
                <div className={styles.section}>
                    <h4>Step 1: NPM Login</h4>
                    <p>Authenticate with the PwC JFrog private registry:</p>

                    <div className={styles.commandContainer}>
                        <code className={styles.command}>{npmLoginCommand}</code>
                        <Button
                            kind="text"
                            icon={copied ? 'icon-check' : 'icon-copy'}
                            onClick={handleCopyCommand}
                            className={styles.copyButton}>
                            {copied ? 'Copied!' : 'Copy'}
                        </Button>
                    </div>

                    <div className={styles.instructions}>
                        <ol>
                            <li>Run the command above in your terminal</li>
                            <li>Enter your PwC credentials when prompted</li>
                            <li>Use your PwC email address</li>
                        </ol>
                    </div>
                </div>

                <div className={styles.section}>
                    <h4>Step 2: Install MCP Server</h4>
                    <p>Enter your project working directory to generate the MCP installation deeplink:</p>

                    <div className={styles.inputContainer}>
                        <Input
                            title="Project Directory"
                            placeholder="e.g., /Users/username/projects/my-project"
                            value={projectDir}
                            onChange={handleProjectDirChange}
                            className={styles.projectDirInput}
                        />
                        <Button
                            kind="primary"
                            onClick={handleGenerateMcpDeeplink}
                            disabled={!projectDir.trim()}
                            className={styles.generateButton}>
                            Generate Deeplink
                        </Button>
                    </div>

                    {mcpDeeplink && (
                        <div className={styles.deeplinkContainer}>
                            <p>
                                <strong>MCP Installation Deeplink:</strong>
                            </p>
                            <div className={styles.deeplinkActions}>
                                <Button
                                    kind="primary"
                                    icon="icon-external-link"
                                    onClick={() => window.open(mcpDeeplink, '_blank')}
                                    className={styles.openDeeplinkButton}>
                                    Open Installation Link
                                </Button>
                                <Button
                                    kind="text"
                                    icon={copied ? 'icon-check' : 'icon-copy'}
                                    onClick={handleCopyMcpDeeplink}
                                    className={styles.copyButton}>
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                            <div className={styles.instructions}>
                                <ol>
                                    <li>Click "Open Installation Link" to install the MCP server</li>
                                    <li>Follow the installation prompts in Cursor</li>
                                    <li>Or copy the link to install manually</li>
                                </ol>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.section}>
                    <h4>Step 3: Open in Cursor</h4>
                    <p>After MCP installation, click "Open in Cursor" to launch the project.</p>

                    <div className={styles.note}>
                        <span className="Appkit4-icon icon-info-circle" />
                        <p>
                            <strong>Note:</strong> Ensure Cursor is installed and the MCP server is properly configured.
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default CursorInstructionsModal;
